import { ExternalLinkType, InferenceEngineFileTypes, InferenceStatus, InterfaceTypes } from '../enums';
import { ValidationErrors } from './ValidationErrors';

export enum DataTypes {
  Boolean = 0,
  Integer = 10,
  Float = 20,
  FloatArray = 21,
  String = 30,
  File = 40,
  Dictionary = 50
}

export enum InputOptionsTypes {
  Data = 0,
}

export enum BaseDataEntityTypes {
  ModelDefinition, // the model itself
  Repository, // repository holding models (a repo can be owned by a user or a organization)
  UserInfo, // information about individual users
  OrganizationInfo, // concept of organization account (which users can be added into)
  RepositoryAccessRight, // defines repository access for specific users
  OrganizationAccessRight, // defines organization access for specific users
  ModelSocialStatus, // tracks social data for a particular model
  UserSocialStatus, // tracks social data that a particular user has entered into the system (i.e. what things they have liked, etc.)
  ModelUsage, // tracks how often a model is used
  UserModelUsage, // tracks what model (and how often) a user uses a model
  Account, // used for tracking money/credits, etc. relating to a user or organization
  ApiKeyInfo, // API keys that can be associated with a user
  ModelRequest,
  Unknown,
}

export enum Roles {
  ReadOnly,
  Designer,
  Admin,
}

export enum ModelTypes {
  HostedModel = 'HostedModel', // UnityPredict-hosted models
  IndexedModel = 'IndexedModel', // models that are referencing an external website/source
  IndexedService = 'IndexedService', // models that represent a managed service
  ALLMODEL = 'AllModels',
}

export enum ModelRequestTypes {
  UnityPredict = 'UnityPredict',
  BasicPublicPost = 'BasicPublicPost',
}

export interface ClientSettings {
  clientType: String;
  auth0ClientId: String;
  auth0DomainName: String;
}

export interface ModelFileStorageInfo {
  filePaths: { [key: string]: string };
}

export interface PerformanceStats {
  matrics: Record<string, number>;
}

export interface SocialStatusInfo {
  averageStarRating: number;
}

export interface OutcomeOptions {
  optionFriendlyName: string;
  optionValue: string;
}

export interface OutcomeInfo {
  name: string;
  outcomeDescription: string;
  outcomeType: DataTypes;
  options: OutcomeOptions[];
  enableOutcomeMultiline: boolean;
}

export interface InputInfo {
  name: string;
  inputDescription: string;
  inputType: DataTypes;
  options: any[];
}

export interface BaseDataEntityDto {
  id: string;
  entityType: BaseDataEntityTypes;
}

export interface BaseModelDefinitionDto {
  modelId: string;
  modelName: string;
  modelDescription: string;
  author: string;
  modelType: ModelTypes;
  performanceScore: string;
  performanceStats: PerformanceStats;
  socialStatus: SocialStatusInfo;
  favIconUrl: string;
}

export interface ModelDefinitionDto extends BaseModelDefinitionDto, BaseDataEntityDto {
  [x: string]: {};
  modelLongDescription: string;
  externalRedirectUrl: string;
  parentRepositoryId: string;
  parentRepositoryName: string;
  active: boolean;
  usageCharge: number;
  isPublic: boolean;
  totalCost: number;
  totalViews: number;
  modelData: ModelFileStorageInfo;
  aiEngineConfig: AIEngineConfiguration;
  permissions?:[];
  pricingInfo: ModelPricingInfo;
  visibility: Visibility;
  modelCategory: string;
  tags: TagInfo[];
  supportingLinks: ExternalLinkInfo[];
  pricingInfoToDisplay: string;
  longRunningInference: false;
  variableDisplayOptions: VariableDisplayOptions;
  needsContext:boolean;
  interfaceType:InterfaceTypes;
  interfaceDisplayOptions:InterfaceDisplayOptions;
  websiteUrl?: string;
}

export interface InterfaceDisplayOptions {
  chatbotStartMessage: string;
}
export interface VariableDisplayOptions {
  [key: string]: {
    enableMultiLineText?: boolean;
    enablePictureViewer?: boolean;
  };
}

export interface ExternalLinkInfo {
  linkType: ExternalLinkType;
  url: string;
}
enum TagType {
  ApplicationType = 'ApplicationType',
}
export interface TagInfo {
  tagType: TagType;
  key: string;
  displayName: string;
}
export enum Visibility {
  // Active = 'Active',
  UnderReview = 'UnderReview',
  Public = 'Public',
  Private = 'Private',
}

export interface ModelPricingInfo {
  servicePricingInfo: string;
  perInferenceUsageCharge: number;
  modelOwnerPaysComputeCost:boolean;
  noChargeOnInferenceWithError:boolean;
  skipUsageChargeInSameCtx:boolean;
}
export interface AutoGeneratedModelPayload {
  targetRepositoryId: string;
  websiteUrl: string;
}

export interface AIEngineConfiguration {
  inferenceOptions: InferenceOptions;
  predictEngineType: string;
  inputs: InputInfo[];
  outcomes: OutcomeInfo[];
}
export enum ExternalApiCallMethod {
  Post = 'Get',
  Get = 'get',
}
export interface InferenceOptions {
  tPrefix: string;
  tSuffix: string;
  httpMethod: ExternalApiCallMethod;
  url: string;
  jsonBodyTemplate: string;
  outputExtractors: Record<string, DataExtractorConfig>;
  headers: Record<string, string>;
}

export interface DataExtractorConfig {
  stringExtractor: StringDataExtractor;
  binaryExtractor: BinaryDataExtractor | null;
}

export interface StringDataExtractor {
  regEx: string;
  matchIndex: number;
  groupIndex: number;
}

export interface BinaryDataExtractor {}

export interface ModelDefinitionDtoCompact extends BaseModelDefinitionDto {}

export interface UserInfoDto extends BaseDataEntityDto {
  userId: string;
  firstName: string;
  lastName: string;
  base64ProfilePhoto?: string;
  aboutMe: string;
  displayName: string;
  publicName: string;
  email: string;
  accountInfo: AccountDto;
  userApiKeys: ApiKeyInfoDto[];
  preferences: Preference;
  permittedAppFeatures: string[];
  permissions: [];
  numberOfPublicModels: number;
  totalNumberOfModels: number;
}
export interface Preference {
  activities: UserActivityTarget[];
  receiveRaMNotifications: boolean;
  userRole: string;
  referralSource: string;
  interestApplications: string[];
}
export enum UserActivityTarget {
  Publisher = 'Publisher',
  Discover = 'Discover',
  Inference = 'Inference',
  ModelRequests = 'ModelRequests',
  Advertise = 'Advertise',
  Other = 'Other',
}

export interface AccountDto {
  entityType: BaseDataEntityTypes;
  currentAccountValue: number;
  id: string;
}

export interface ApiKeyInfoDto {
  entityType?: string;
  ownerUserId?: string;
  id?: string;
  apiKey: string;
  keyDescription: string;
}

export interface RepositoryDto extends BaseDataEntityDto {
  repositoryId: string;
  repositoryName: string;
  repositoryDescription: string;
  ownerId: string;
  permissions: string[];
  repositoryAccessRights: RepositoryAccessRightDto[];
}
export interface RepositoryAccessRightDto extends BaseDataEntityDto {
  userId: string;
  repositoryId: string;
  roles: Roles[];
}

export interface OrganizationAccessRightDto extends BaseDataEntityDto {
  userId: string;
  organizationId: string;
  roles: Roles[];
  userDisplayName: string;
}

export interface OrganizationInfoDto extends BaseDataEntityDto {
  organizationId: string;
  organizationName: string;
  users: OrganizationAccessRightDto[];
  accountInfo: AccountDto;
  contactEmail: string;
  aboutMe: string;
  permissions: string[];
  base64ProfilePhoto: string;
  publicName: string;
  websiteUrl: string;
}

export interface SearchResults {
  totalCount: number;
  paginationId: string;
}

export interface ModelSearchResults extends SearchResults {
  results: ModelDefinitionDtoCompact[];
}

export interface ContactUs {
  topic: string;
  email: string;
  message: string;
}

export interface ClaimModel {
  RequestingUserId: string;
  modelId: string;
  companyName: string;
  contactName: string;
  contactemail: string;
  reason: string;
}

export interface ModelReview {
  id?: string;
  reviewTitle?: string;
  reviewText?: string;
  starRating: number;
  userPublicName?: string;
}

export interface ModelRequestSocialStatusInfo {
  likes: number;
}
export interface ModelRequestDtoCompact {
  modelRequestId: string;
  requestDescription: string;
  requestTitle: string;
  socialStatus: ModelRequestSocialStatusInfo;
}

export interface ModelRequestDto extends BaseDataEntityDto {
  modelRequestId: string;
  requestDescription: string;
  requestTitle: string;
  socialStatus: ModelRequestSocialStatusInfo;
  modelRequestType: ModelRequestTypes;
  requestingUserId: string;
  isActive: boolean;
  activatedOn: Date | null;
  createdOn: Date | null;
  surveyResponse: ModelRequestSurveryResponse;
  associatedFiles: ModelRequestFileInfo[];
  errors: ValidationErrors;
  mode: string;
  status: ModelRequestStatus;
}
export enum ModelRequestStatus {
  Draft = 'Draft',
  UnderReview = 'UnderReview',
  Active = 'Active',
  Deleted = 'Deleted',
}

export interface ModelRequestSurveryResponse {
  predictionsPerMonth: PredictionPerMonthRange;
  optionalContactInfo?: ContactInfo;
  canShareDataPublically: boolean;
  suggestedInputs: [{ name: string; description: string }];
  suggestedOutputs: [{ name: string; description: string }];
}
export enum PredictionPerMonthRange {
  OnlyOnce = 0,
  LessThan10 = 10,
  LessThan1000 = 1000,
  LessThan10000 = 10000,
  MoreThan10000 = 50000,
}
export interface ContactInfo {
  contactName: string;
  companyName: string;
  contactPhoneNumber: string;
  contactEmail: string;
}
export interface ModelRequestFileInfo extends FileStatus {
  fileId: string;
  fileTitle: string;
  fileDescription: string;
  storedFilePath: string;
  fileStatus: string;
  fileName: string;
}

export interface FileStatus {
  status: string;
  jobProgress: number;
}

export interface BalanceTransferRequest {
  targetAccountId: string;
  requestingUserId: string;
  amount: number;
  'transfer type': string;
}

export interface PromoCodeRequest {
  accountId: string;
  userId: string;
  promoCode: string;
}
export interface BaseUnityPredictEntityDto {}
export interface AppEngineDefinitionDto {
  createdDate: Date;
  modifiedDate: Date;
  engineId: string | null;
  engineName: string;
  parentRepositoryId: string;
  appEnginePlatform: string;
  packageDefinitionFileName: string;
  files: InferenceEngineFileInfo[];
  engineDescription: string;
  requirementFileStr: string;
  memoryRequirement: number;
  storageRequirement: number;
  enableChainedModels: boolean;
  status: string;
  modelOwnerPaysForInference: boolean;
}

export interface InferenceEngineFileInfo {
  fileType: InferenceEngineFileTypes;
  fileName: string;
}

/**
 * {
    "contextId": "d00f1cc5-7114-48fc-bcfb-6ffecfc1f502",
    "requestId": "465c0561-9a9e-4617-b14f-fea434c3068e",
    "errorMessages": null,
    "logMessages": null,
    "computeTime": "00:00:00",
    "computeCost": -1.0,
    "outcomeValues": {},
    "status": "Processing"
}
 */
export interface InferenceResponse {
  contextId: string;
  requestId: string;
  errorMessages: string;
  logMessages: string;
  computeTime:string;
  computeCost:number;
  outcomeValues:any;
  outcomes:any;
  status:InferenceStatus;
}
